import { useEffect, useRef } from "react";
import * as THREE from "three";

// SACVPN's real 14-node network. lat/long drive the marker positions so the
// globe shows the actual footprint, not decoration.
const NODES = [
  { name: "Virginia", lat: 38.9, lon: -77.0 },
  { name: "Texas", lat: 32.8, lon: -96.8 },
  { name: "Miami", lat: 25.8, lon: -80.2 },
  { name: "Oregon", lat: 45.8, lon: -122.7 },
  { name: "Montreal", lat: 45.5, lon: -73.6 },
  { name: "London", lat: 51.5, lon: -0.1 },
  { name: "Frankfurt", lat: 50.1, lon: 8.7 },
  { name: "Gravelines", lat: 51.0, lon: 2.1 },
  { name: "Strasbourg", lat: 48.6, lon: 7.8 },
  { name: "Warsaw", lat: 52.2, lon: 21.0 },
  { name: "Tokyo", lat: 35.7, lon: 139.7 },
  { name: "Singapore", lat: 1.35, lon: 103.8 },
  { name: "Mumbai", lat: 19.1, lon: 72.9 },
  { name: "Sydney", lat: -33.9, lon: 151.2 },
];

// Connection arcs between hubs (indices into NODES) that sweep across the globe.
const ARCS = [
  [0, 1], [1, 2], [0, 3], [0, 4], [0, 5], [5, 6], [5, 7],
  [6, 9], [7, 8], [3, 10], [10, 11], [11, 12], [11, 13],
];

function latLonToVec3(lat, lon, r) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
}

export default function GlobeHero({ className = "" }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let renderer;
    let raf = 0;
    const cleanupFns = [];

    try {
      const W = mount.clientWidth || 600;
      const H = mount.clientHeight || 600;
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(42, W / H, 0.1, 100);
      camera.position.z = 3.05;

      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(W, H);
      mount.appendChild(renderer.domElement);

      const world = new THREE.Group();
      world.rotation.x = 0.28;
      scene.add(world);

      const R = 1;

      // Dotted globe: fibonacci sphere of small points.
      const DOTS = 2200;
      const gPos = new Float32Array(DOTS * 3);
      const golden = Math.PI * (3 - Math.sqrt(5));
      for (let i = 0; i < DOTS; i++) {
        const y = 1 - (i / (DOTS - 1)) * 2;
        const rad = Math.sqrt(1 - y * y);
        const t = golden * i;
        gPos[i * 3] = Math.cos(t) * rad * R;
        gPos[i * 3 + 1] = y * R;
        gPos[i * 3 + 2] = Math.sin(t) * rad * R;
      }
      const gGeo = new THREE.BufferGeometry();
      gGeo.setAttribute("position", new THREE.BufferAttribute(gPos, 3));
      const gMat = new THREE.PointsMaterial({ color: 0x2b6f9e, size: 0.014, transparent: true, opacity: 0.55, sizeAttenuation: true });
      const globe = new THREE.Points(gGeo, gMat);
      world.add(globe);

      // Faint inner sphere for depth.
      const shell = new THREE.Mesh(
        new THREE.SphereGeometry(R * 0.985, 48, 48),
        new THREE.MeshBasicMaterial({ color: 0x061626, transparent: true, opacity: 0.85 })
      );
      world.add(shell);

      // Soft circular sprite for glowing markers + pulses.
      const glowCanvas = document.createElement("canvas");
      glowCanvas.width = glowCanvas.height = 64;
      const gx = glowCanvas.getContext("2d");
      const grad = gx.createRadialGradient(32, 32, 0, 32, 32, 32);
      grad.addColorStop(0, "rgba(120,230,255,1)");
      grad.addColorStop(0.35, "rgba(80,200,255,0.85)");
      grad.addColorStop(1, "rgba(80,200,255,0)");
      gx.fillStyle = grad;
      gx.beginPath();
      gx.arc(32, 32, 32, 0, Math.PI * 2);
      gx.fill();
      const glowTex = new THREE.CanvasTexture(glowCanvas);

      // Node markers.
      const nodeVecs = NODES.map((n) => latLonToVec3(n.lat, n.lon, R * 1.008));
      const markers = new THREE.Group();
      nodeVecs.forEach((v) => {
        const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
        s.position.copy(v);
        s.scale.setScalar(0.09);
        markers.add(s);
      });
      world.add(markers);

      // Arcs + travelling pulses.
      const pulses = [];
      ARCS.forEach(([a, b]) => {
        const A = nodeVecs[a], B = nodeVecs[b];
        const mid = A.clone().add(B).multiplyScalar(0.5);
        const lift = 1 + A.distanceTo(B) * 0.35;
        mid.normalize().multiplyScalar(R * lift);
        const curve = new THREE.QuadraticBezierCurve3(A, mid, B);
        const pts = curve.getPoints(60);
        const geo = new THREE.BufferGeometry().setFromPoints(pts);
        const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.32 }));
        world.add(line);
        const pulse = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: 0xaef0ff }));
        pulse.scale.setScalar(0.06);
        world.add(pulse);
        pulses.push({ curve, sprite: pulse, t: Math.random(), speed: 0.0016 + Math.random() * 0.0016 });
      });

      // Starfield.
      const SN = 380;
      const sPos = new Float32Array(SN * 3);
      for (let i = 0; i < SN; i++) {
        const rr = 6 + Math.random() * 12;
        const th = Math.random() * Math.PI * 2;
        const ph = Math.acos(2 * Math.random() - 1);
        sPos[i * 3] = rr * Math.sin(ph) * Math.cos(th);
        sPos[i * 3 + 1] = rr * Math.sin(ph) * Math.sin(th);
        sPos[i * 3 + 2] = rr * Math.cos(ph);
      }
      const starGeo = new THREE.BufferGeometry();
      starGeo.setAttribute("position", new THREE.BufferAttribute(sPos, 3));
      const stars = new THREE.Points(starGeo, new THREE.PointsMaterial({ color: 0x9fb6d0, size: 0.03, transparent: true, opacity: 0.5 }));
      scene.add(stars);

      // Interaction: gentle auto-rotate + mouse parallax.
      let targetX = 0, targetY = 0.28;
      function onMove(e) {
        const r = mount.getBoundingClientRect();
        targetX = ((e.clientX - r.left) / r.width - 0.5) * 0.5;
        targetY = 0.28 - ((e.clientY - r.top) / r.height - 0.5) * 0.4;
      }
      window.addEventListener("mousemove", onMove, { passive: true });
      cleanupFns.push(() => window.removeEventListener("mousemove", onMove));

      function onResize() {
        const w = mount.clientWidth, h = mount.clientHeight;
        if (!w || !h) return;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      }
      window.addEventListener("resize", onResize, { passive: true });
      cleanupFns.push(() => window.removeEventListener("resize", onResize));

      const tmp = new THREE.Vector3();
      function animate() {
        raf = requestAnimationFrame(animate);
        world.rotation.y += 0.0016;
        world.rotation.x += (targetY - world.rotation.x) * 0.04;
        world.rotation.y += (targetX) * 0.0;
        stars.rotation.y += 0.0004;
        for (const p of pulses) {
          p.t += p.speed;
          if (p.t > 1) p.t -= 1;
          p.curve.getPointAt(p.t, tmp);
          p.sprite.position.copy(tmp).applyEuler(world.rotation);
        }
        renderer.render(scene, camera);
      }
      animate();

      cleanupFns.push(() => {
        cancelAnimationFrame(raf);
        gGeo.dispose(); gMat.dispose(); glowTex.dispose();
        renderer.dispose();
        if (renderer.domElement && renderer.domElement.parentNode) {
          renderer.domElement.parentNode.removeChild(renderer.domElement);
        }
      });
    } catch (err) {
      // No WebGL (or prerender headless without GL): the CSS gradient fallback
      // stays visible and the hero text still renders. Never throw.
      if (typeof console !== "undefined") console.warn("GlobeHero: WebGL unavailable, using fallback", err && err.message);
    }

    return () => cleanupFns.forEach((fn) => fn());
  }, []);

  return (
    <div
      ref={mountRef}
      className={className}
      aria-hidden="true"
      style={{ position: "relative", width: "100%", height: "100%", minHeight: 420 }}
    />
  );
}
